import { ReactComponent as File } from "@/assets/icons/file.svg";
import { ReactComponent as Measure } from "@/assets/icons/measure.svg";
import { ReactComponent as Stats } from "@/assets/icons/stats.svg";
import { ReactComponent as ActivitySVG } from "@/assets/img/workbookContents/activity.svg";
import { ReactComponent as FoodSVG } from "@/assets/img/workbookContents/food.svg";
import { ReactComponent as HealthSVG } from "@/assets/img/workbookContents/health.svg";
import i18n from "@/localization/i18next";

import { CurrencyCodes } from "./currency";

const BC_ORIGINAL_AU_PRICE = 73;
const BC_ORIGINAL_CA_PRICE = 67;
const BC_ORIGINAL_PRICE = 49;
const BC_ORIGINAL_EU_PRICE = 45;
const BC_ORIGINAL_MX_PRICE = 549.99;
const BUNDLE_ORIGINAL_PRICE = "89";
const BUNDLE_REGULAR_PRICE = "49.99";
const BUNDLE_DISCOUNT_PRICE = "29.99";
export const BC_WORKBOOK_BUNDLE_PRICE = 82.99;

export const BODY_SCAN_DISCOUNT = 20;
export const BC_WORKBOOK_BUNDLE_DISCOUNT = 25;

export const BC_MIN_AGE = 18;
export const BC_MAX_AGE = 65;

export const BUNDLE_PRICES = {
    original: BUNDLE_ORIGINAL_PRICE,
    regular: BUNDLE_REGULAR_PRICE,
    discount: BUNDLE_DISCOUNT_PRICE,
};

export const enum BCPricePlansIds {
    zing_bodycomposition_freebie = "zing_bodycomposition_freebie",
    zing_bodycomposition_00_50_usd = "zing_bodycomposition_00_50_usd",
    zing_bodycomposition_19_99_usd = "zing_bodycomposition_19_99_usd",
    zing_bodycomposition_28_99_usd = "zing_bodycomposition_28_99_usd",
    zing_bodycomposition_19_99_gbp = "zing_bodycomposition_19_99_gbp",
    zing_bodycomposition_28_99_gbp = "zing_bodycomposition_28_99_gbp",
    zing_bodycomposition_28_99_aud = "zing_bodycomposition_28_99_aud",
    zing_bodycomposition_42_99_aud = "zing_bodycomposition_42_99_aud",
    zing_bodycomposition_25_99_cad = "zing_bodycomposition_25_99_cad",
    zing_bodycomposition_38_99_cad = "zing_bodycomposition_38_99_cad",
    zing_bodycomposition_27_99_eur = "zing_bodycomposition_27_99_eur",
    zing_bodycomposition_18_99_eur = "zing_bodycomposition_18_99_eur",
    zing_bodycomposition_329_99_mxn = "zing_bodycomposition_329_99_mxn",
    zing_bodycomposition_229_99_mxn = "zing_bodycomposition_229_99_mxn",
}

export const BC_WORKBOOK_BUNDLE_PLANS = {
    regular: "zing_bundle_bodyscan_workbook_bundle_v2_49_99_usd",
    discount: "zing_bundle_bodyscan_workbook_bundle_v2_37_49_usd",
};

export const BC_IDENT_PLANS: Record<
    CurrencyCodes,
    {
        discount: BCPricePlansIds | string;
        regular: BCPricePlansIds | string;
        free?: BCPricePlansIds | string;
    }
> = {
    [CurrencyCodes.GBP]: {
        discount: BCPricePlansIds.zing_bodycomposition_19_99_gbp,
        regular: BCPricePlansIds.zing_bodycomposition_28_99_gbp,
    },
    [CurrencyCodes.USD]: {
        discount: BCPricePlansIds.zing_bodycomposition_19_99_usd,
        regular: BCPricePlansIds.zing_bodycomposition_28_99_usd,
        free: BCPricePlansIds.zing_bodycomposition_00_50_usd,
    },
    [CurrencyCodes.AUD]: {
        discount: BCPricePlansIds.zing_bodycomposition_28_99_aud,
        regular: BCPricePlansIds.zing_bodycomposition_42_99_aud,
    },
    [CurrencyCodes.CAD]: {
        discount: BCPricePlansIds.zing_bodycomposition_25_99_cad,
        regular: BCPricePlansIds.zing_bodycomposition_38_99_cad,
    },
    [CurrencyCodes.EUR]: {
        discount: BCPricePlansIds.zing_bodycomposition_18_99_eur,
        regular: BCPricePlansIds.zing_bodycomposition_27_99_eur,
    },
    [CurrencyCodes.MXN]: {
        discount: BCPricePlansIds.zing_bodycomposition_229_99_mxn,
        regular: BCPricePlansIds.zing_bodycomposition_329_99_mxn,
    },
};

export const BC_ORIGINAL_PRICES: Record<CurrencyCodes, number> = {
    [CurrencyCodes.GBP]: BC_ORIGINAL_PRICE,
    [CurrencyCodes.USD]: BC_ORIGINAL_PRICE,
    [CurrencyCodes.AUD]: BC_ORIGINAL_AU_PRICE,
    [CurrencyCodes.CAD]: BC_ORIGINAL_CA_PRICE,
    [CurrencyCodes.EUR]: BC_ORIGINAL_EU_PRICE,
    [CurrencyCodes.MXN]: BC_ORIGINAL_MX_PRICE,
};

export const getIdentPlans = (currency = CurrencyCodes.USD) => {
    return BC_IDENT_PLANS[currency];
};

export const BODY_SCAN_INCLUDED = () => [
    {
        id: "fitnessPlan",
        icon: ActivitySVG,
        text: i18n.t("body_scan_analysis"),
    },
    {
        id: "mealPlan",
        icon: FoodSVG,
        text: i18n.t("macronutrient_calorie_plan"),
    },
    {
        id: "selfCare",
        icon: HealthSVG,
        text: i18n.t("deeper_fitness_personalization"),
    },
];

export const REPORT_DATA = [
    i18n.t("body_fat_percentage"),
    i18n.t("lean_mass_index"),
    i18n.t("fat_mass_index"),
    i18n.t("resting_metabolic_rate"),
    i18n.t("macronutrient_plan"),
];

export const WHY_ZING_INFO = [
    {
        title: i18n.t("comprehensive_body_analysis"),
        description: i18n.t("body_analysis_explanation"),
    },
    {
        title: i18n.t("personalized_measurements"),
        description: i18n.t("personalized_measurements_explanation"),
    },
    {
        title: i18n.t("reduced_fluctuations"),
        description: i18n.t("smart_scales_explanation"),
    },
];

export const WHY_INFO_CARDS = [
    {
        icon: Measure,
        description: i18n.t("measure_body_fat_message"),
    },
    {
        icon: Stats,
        description: i18n.t("compare_insights_message"),
    },
    {
        icon: File,
        description: i18n.t("track_results_message"),
    },
];
